import React, { useMemo } from "react";
import JeaInput from "@/components/jea/JeaInput";
import { STANDARD_PRICING, PRO_PLANS } from "@/lib/pricing";

const BillingStep = ({ data, patchSection, accountType }) => {
  const b = data.billing || {};
  const setField = (k, v) => patchSection("billing", { [k]: v });

  const summary = useMemo(() => {
    if (accountType === "standard") {
      const path = data.plan?.path;
      const dc = data.plan?.debtor_count;
      if (!path || !dc) return null;
      return {
        line1: `Standard — Path ${path}`,
        line2: `${dc} ${dc === 1 ? "judgment" : "judgments"}`,
        total: `$${STANDARD_PRICING[dc][path]}`,
      };
    }
    const cad = data.plan?.cadence;
    if (!cad) return null;
    const plan = PRO_PLANS.find((p) => p.key === cad);
    return {
      line1: `PRO — ${cad === "monthly" ? "Monthly" : "Annual"}`,
      line2: "Unlimited judgments",
      total: `$${plan.price.toLocaleString()}`,
    };
  }, [data, accountType]);

  const onCardNumber = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 16);
    const spaced = digits.replace(/(.{4})/g, "$1 ").trim();
    setField("card_number", spaced);
  };
  const onExpiry = (e) => {
    let v = e.target.value.replace(/[^\d/]/g, "");
    if (v.length === 2 && b.expiry?.length === 1) v += "/";
    if (v.length === 3 && !v.includes("/")) v = v.slice(0, 2) + "/" + v.slice(2);
    v = v.slice(0, 5);
    setField("expiry", v);
  };

  return (
    <div className="space-y-6" data-testid="billing-step">
      <div className="space-y-2">
        <h2 className="text-white font-bold text-[22px]">Complete Your Setup</h2>
        <p className="text-text-secondary text-[15px]">
          Enter your payment details to activate your subscription. Broker query costs are billed
          per query.
        </p>
      </div>

      {summary && (
        <div className="bg-bg-tertiary rounded-[8px] px-5 py-4 border border-border-default flex items-start justify-between" data-testid="billing-summary">
          <div className="text-[14px] space-y-1">
            <div className="text-white font-semibold">{summary.line1}</div>
            <div className="text-text-secondary">{summary.line2}</div>
          </div>
          <div className="text-right">
            <div className="jea-caption">Total Today</div>
            <div className="jea-mono text-accent-pink font-bold text-[20px]" data-testid="billing-total">
              {summary.total}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <JeaInput
          placeholder="Cardholder Name"
          value={b.cardholder || ""}
          onChange={(e) => setField("cardholder", e.target.value)}
          autoComplete="cc-name"
          data-testid="billing-cardholder"
        />
        <JeaInput
          placeholder="Card Number"
          value={b.card_number || ""}
          onChange={onCardNumber}
          inputMode="numeric"
          autoComplete="cc-number"
          className="jea-mono"
          data-testid="billing-card-number"
        />
        <div className="grid grid-cols-2 gap-3">
          <JeaInput
            placeholder="MM/YY"
            value={b.expiry || ""}
            onChange={onExpiry}
            inputMode="numeric"
            autoComplete="cc-exp"
            className="jea-mono"
            data-testid="billing-expiry"
          />
          <JeaInput
            placeholder="CVC"
            value={b.cvc || ""}
            onChange={(e) => setField("cvc", e.target.value.replace(/\D/g, "").slice(0, 4))}
            inputMode="numeric"
            autoComplete="cc-csc"
            className="jea-mono"
            data-testid="billing-cvc"
          />
        </div>
        <JeaInput
          placeholder="Billing ZIP"
          value={b.zip || ""}
          onChange={(e) => setField("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
          inputMode="numeric"
          autoComplete="postal-code"
          className="jea-mono"
          data-testid="billing-zip"
        />
      </div>

      <p className="text-[11px] text-text-secondary text-center">
        This is a demo billing screen. No real card will be charged. Live Stripe integration
        activates when credentials are provided.
      </p>
    </div>
  );
};

export default BillingStep;
